import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { Loading } from '../components';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Briefcase } from 'lucide-react';
import { isJobViewed } from '../utils/viewedStore';
import { useTranslation } from 'react-i18next';

const HelperRecommendedJobsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    const load = async () => {
      try {
        const profileRes: any = await apiService.helpers.getByUserId(user.id!);
        if (!profileRes.success || !profileRes.data?.id) return;
        const res: any = await apiService.helpers.getRecommendedJobs(profileRes.data.id, 100);
        if (res.success && res.data) setMatches(res.data.filter((m: any) => m.matchPercent >= 80));
      } catch (e) {
        console.error('Error loading recommended jobs:', e);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [user?.id]);

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <Button variant="ghost" className="gap-2" onClick={() => navigate('/home')}>
        <ArrowLeft className="h-4 w-4" />
        {t('common.back', '返回')}
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{t('helperRecommendedJobs.title', '最匹配職位')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('helperRecommendedJobs.subtitle', '匹配度 80% 或以上的職位')}</p>
        </CardHeader>
        <CardContent>
          {matches.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-center">
              <Briefcase className="mb-4 h-12 w-12 text-muted-foreground/40" />
              <p className="text-muted-foreground">{t('helperRecommendedJobs.noMatches', '暫無符合條件的職位，請完善個人資料以提升匹配度')}</p>
              <Button className="mt-4" variant="outline" onClick={() => navigate('/profile')}>{t('helperRecommendedJobs.completeProfile', '完善個人資料')}</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{t('helperRecommendedJobs.count', '共 {{count}} 個符合條件的職位', { count: matches.length })}</p>
              {matches.map((m: any) => (
                <Card
                  key={m.jobPosting.id}
                  className="cursor-pointer transition-shadow hover:shadow-md"
                  onClick={() => navigate(`/job-postings/${m.jobPosting.id}/detail`)}
                >
                  <CardContent className="flex items-center gap-4 p-5">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-foreground">{m.jobPosting.headline || m.jobPosting.title}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {m.jobPosting.location} · {m.jobPosting.workType}
                      </p>
                      {m.matchedReasons?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {m.matchedReasons.slice(0, 3).map((r: string, i: number) => (
                            <span key={i} className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">{r}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {isJobViewed(m.jobPosting.id) && (
                        <Badge variant="outline" className="text-[11px] text-muted-foreground">{t('common.viewed', '已瀏覽')}</Badge>
                      )}
                      <Badge variant={m.matchPercent >= 90 ? 'default' : 'secondary'} className="text-sm">
                        {t('common.matchPercent', '{{n}}% 符合', { n: m.matchPercent })}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HelperRecommendedJobsPage;
